import React, { ReactNode, useEffect, useRef, useState } from 'react'
import { Box, Typography, CircularProgress } from '@mui/material'
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward'
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward'
import { BORDER_BLACK, BORDER_BLUE, BORDER_DIVIDER, COLOR_PRIMARY_HOVER, COLOR_PRIMARY_HOVER1, COLOR_PRIMARY_HOVER2 } from '../common/theme'
// import { BORDER_BLACK, BORDER_BLUE } from '../common/theme'

// Reusable, sortable, resizable table.
// Caller provides:
//   • cols: definition with custom render per column
//   • rows + rowKey
//   • sort + onSortChange (controlled - caller does the actual sorting)
// The table handles column-width state, the divider drag-to-resize handles,
// the sort indicators, and the click-through wiring.

export interface DataTableColumn<T> {
  id: string
  label?: string                                  // hidden for icon columns
  width: number                                   // initial width in px
  minWidth?: number                               // for flex columns
  flex?: number                                   // 0/undefined = fixed-width
  // Optional explicit alignment. `alignRight` stays as the back-compat
  // boolean shortcut; `align` lets a column centre itself (useful for
  // pill / arc cells that are visually balanced).
  align?: 'left' | 'center' | 'right'
  // Icon columns are spacers/icon-only: fixed width, no label, no sort, no
  // resize handle. Use them for checkboxes, file-type icons, indexed status,
  // 3-dots menus, etc.
  iconColumn?: boolean
  // Disable sort affordance even on a non-icon column.
  sortable?: boolean
  // Per-cell renderer - full control over what shows in each cell.
  render: (row: T) => ReactNode
  // Optional custom header - supersedes the `label` Typography when set.
  // Use this when the column header is itself a control (e.g. a master
  // padlock above a column of per-row padlocks).
  headerRender?: () => ReactNode
}

export interface DataTableSort {
  colId: string
  dir: 'asc' | 'desc'
}

export interface DataTableProps<T> {
  rows: T[]
  cols: DataTableColumn<T>[]
  rowKey: (row: T, index: number) => string | number
  rowSx?: (row: T) => any
  rowDataAttrs?: (row: T) => Record<string, string | number | boolean | undefined>
  rowTestId?: string
  onRowClick?: (row: T) => void
  sort?: DataTableSort
  onSortChange?: (colId: string, dir: 'asc' | 'desc') => void
  emptyContent?: ReactNode
  loading?: boolean
  testIdPrefix?: string
  // Optional extra block rendered beneath each row, full table width. Use for
  // expand/collapse details. Caller manages the expanded state.
  rowExtra?: (row: T) => ReactNode
  // Paints an `action.hover` background on the row under the cursor.
  // Defaults to whether `onRowClick` is wired — an unclickable table
  // stays flat unless the caller explicitly opts in.
  hoverHighlight?: boolean
}

function colSx(col: DataTableColumn<any>, currentWidth: number, isFlex: boolean) {
  return isFlex
    ? { flex: col.flex ?? 1, minWidth: col.minWidth ?? 60 }
    : { width: currentWidth, flexShrink: 0 }
}

// Resolve the column's `justifyContent` value. `align` wins when set;
// otherwise the legacy `alignRight` boolean still works.
function justifyFor(col: DataTableColumn<any>): 'flex-start' | 'center' | 'flex-end' {
  if (col.align === 'center') return 'center'
  if (col.align === 'right') return 'flex-end'
  return 'flex-start'
}

export function DataTable<T>({
  rows, cols, rowKey, rowSx, rowDataAttrs, rowTestId,
  onRowClick, sort, onSortChange, emptyContent, loading,
  testIdPrefix = 'datatable', rowExtra,
  hoverHighlight,
}: DataTableProps<T>) {
  // Callers can opt hover-highlight in explicitly; when unset it
  // matches whether the row is clickable — feels natural for the
  // legacy call sites that never touched the prop.
  const showRowHover = hoverHighlight ?? !!onRowClick

  // Internal width state - seeded from each col's initial width. Keyed by
  // col.id so re-renders with the same id keep the user's resize.
  const [widths, setWidths] = useState<Record<string, number>>(() => {
    const m: Record<string, number> = {}
    cols.forEach(c => { m[c.id] = c.width })
    return m
  })

  // Once a flex column is dragged, it freezes to a fixed width.
  const [frozenFlex, setFrozenFlex] = useState<Record<string, boolean>>({})

  const colResizingRef = useRef<{ id: string; startX: number; startW: number } | null>(null)

  // Track the rows container's vertical-scrollbar width so the header can
  // reserve an equivalent right gutter. Without this the rightmost column
  // sits behind the scrollbar in the rows but flush in the header and the
  // two drift visibly out of alignment until the user drags a column.
  const rowsRef = useRef<HTMLDivElement>(null)
  const [scrollbarPad, setScrollbarPad] = useState(0)
  useEffect(() => {
    const el = rowsRef.current
    if (!el) return
    const measure = () => {
      // offsetWidth - clientWidth gives the vertical scrollbar's width
      // when one is visible, 0 otherwise. Works in every modern browser.
      const sw = el.offsetWidth - el.clientWidth
      setScrollbarPad(prev => prev === sw ? prev : sw)
    }
    measure()
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [rows.length, cols.length])

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!colResizingRef.current) return
      const { id, startX, startW } = colResizingRef.current
      const newW = Math.max(40, startW + e.clientX - startX)
      setWidths(prev => ({ ...prev, [id]: newW }))
      setFrozenFlex(prev => prev[id] ? prev : { ...prev, [id]: true })
    }
    const onUp = () => {
      if (!colResizingRef.current) return
      colResizingRef.current = null
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
    }
    document.addEventListener('mousemove', onMove)
    document.addEventListener('mouseup', onUp)
    return () => {
      document.removeEventListener('mousemove', onMove)
      document.removeEventListener('mouseup', onUp)
    }
  }, [])

  const handleResizeStart = (e: React.MouseEvent, colId: string) => {
    e.preventDefault()
    e.stopPropagation()
    // The header cell is the parent of the absolutely-positioned drag handle.
    // Measure its rendered width so flex columns have a real starting size.
    const headerEl = (e.currentTarget as HTMLElement).parentElement
    const startW = headerEl?.offsetWidth ?? widths[colId] ?? 100
    colResizingRef.current = { id: colId, startX: e.clientX, startW }
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
  }

  const handleHeaderClick = (col: DataTableColumn<T>) => {
    if (col.iconColumn) return
    if (col.sortable === false) return
    if (!onSortChange) return
    const isActive = sort?.colId === col.id
    const dir: 'asc' | 'desc' = isActive ? (sort?.dir === 'asc' ? 'desc' : 'asc') : 'asc'
    onSortChange(col.id, dir)
  }

  const isFlexCol = (col: DataTableColumn<T>) =>
    !!col.flex && !frozenFlex[col.id]

  return (
    <Box
      data-testid={testIdPrefix}
      sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
    >

      {/* Header row */}
      <Box sx={{
        display: 'flex', alignItems: 'center', height: 30,
        pl: 1,
        // Reserve the rows container's scrollbar width on the right so
        // columns stay aligned between header and rows. `scrollbarPad`
        // is 0 when the rows aren't overflowing.
        pr: scrollbarPad > 0 ? `calc(${scrollbarPad}px + 8px)` : 1,
        borderBottom: 1, borderColor: 'divider', flexShrink: 0,
        bgcolor: 'action.hover', userSelect: 'none',
        // border: BORDER_BLACK
      }}>
        {cols.map((col, i) => {
          const isLast = i === cols.length - 1
          const isActive = sort?.colId === col.id
          const isSortable = !col.iconColumn && col.sortable !== false && !!onSortChange
          const flex = isFlexCol(col)
          // Icon columns get no divider (they're spacers); the last column
          // also gets no divider since there's nothing after it.
          const showDivider = !col.iconColumn && !isLast
          const isDraggable = showDivider
          return (
            <Box
              key={col.id}
              data-testid={`${testIdPrefix}_header_${col.id}`}
              onClick={() => handleHeaderClick(col)}
              sx={{
                ...colSx(col, widths[col.id] ?? col.width, flex),
                position: 'relative',
                display: 'flex', alignItems: 'center',
                gap: 0.25,
                justifyContent: justifyFor(col),
                py: 0.5,
                // px: 0.5,
                px: justifyFor(col) == 'center' ? 0.5 : 0.75,
                cursor: isSortable ? 'pointer' : 'default',
                // border: BORDER_BLUE
              }}
            >
              {col.headerRender ? (
                col.headerRender()
              ) : !col.iconColumn && col.label && (
                <Typography variant="caption" sx={{
                  height: "16px",
                  fontWeight: 600,
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  opacity: isActive ? 0.9 : 0.65,
                  color: isActive ? 'primary.main' : 'text.primary',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  '&:hover': isSortable ? { bgcolor: 'action.selected' } : {},
                  // border: BORDER_BLACK
                }}>
                  {col.label}
                </Typography>
              )}
              {isActive && isSortable && (
                sort!.dir === 'asc'
                  ? <ArrowUpwardIcon sx={{ fontSize: '0.9rem', color: 'primary.main', opacity: 0.7, flexShrink: 0 }} />
                  : <ArrowDownwardIcon sx={{ fontSize: '0.9rem', color: 'primary.main', opacity: 0.7, flexShrink: 0 }} />
              )}
              {showDivider && (
                <Box
                  onMouseDown={isDraggable ? (e) => handleResizeStart(e, col.id) : undefined}
                  onClick={isDraggable ? (e) => e.stopPropagation() : undefined}
                  sx={{
                    position: 'absolute',
                    height: '25px',
                    right: 0,
                    mr: 0,
                    ml: 'auto',
                    pl: 0.5,
                    // border: BORDER_BLACK,
                    borderRight: isDraggable ? BORDER_DIVIDER : 'none',
                    cursor: isDraggable ? 'col-resize' : 'default',
                    pointerEvents: isDraggable ? 'auto' : 'none',
                    '&:hover': isDraggable ? {
                      bgcolor: 'action.selected',
                      borderRightColor: 'primary.main',
                    } : {},
                  }}
                />
              )}
            </Box>
          )
        })}
      </Box>

      {/* Rows */}
      <Box ref={rowsRef} sx={{ flex: 1, overflowY: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', pt: 4 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          emptyContent ?? (
            <Box sx={{ px: 2, py: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ opacity: 0.45 }}>No items.</Typography>
            </Box>
          )
        ) : (
          rows.map((row, i) => {
            const attrs = rowDataAttrs?.(row) ?? {}
            const extra = rowExtra?.(row)
            return (
              <React.Fragment key={rowKey(row, i)}>
                <Box
                  data-testid={rowTestId}
                  {...attrs}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    display: 'flex', alignItems: 'center',
                    px: 1,
                    py: 0.5,
                    borderBottom: 1, borderColor: 'divider',

                    cursor: onRowClick ? 'pointer' : 'default',
                    '&:hover': showRowHover ? { bgcolor: COLOR_PRIMARY_HOVER1 } : {},
                    ...(rowSx?.(row) ?? {}),
                  }}
                >
                  {cols.map(col => {
                    const flex = isFlexCol(col)
                    return (
                      <Box
                        key={col.id}
                        sx={{
                          ...colSx(col, widths[col.id] ?? col.width, flex),
                          display: 'flex', alignItems: 'center',
                          justifyContent: justifyFor(col),
                          overflow: 'hidden',
                          px: justifyFor(col) == 'center' ? 0.5 : 0.75,
                          // border: BORDER_BLUE
                        }}
                      >
                        {col.render(row)}
                      </Box>
                    )
                  })}
                </Box>
                {extra}
              </React.Fragment>
            )
          })
        )}
      </Box>
    </Box>
  )
}
